import type { ApiError, ApiResponse, ErrorCode } from "@contracts/core";
import { clearToken, getToken } from "../auth/token.js";
import { isEmbedded } from "../lib/appBridge.js";
import { mintSessionAuth } from "../auth/session-auth.js";

export class ApiClientError extends Error {
  readonly code: ErrorCode | "NETWORK_ERROR" | "UNAUTHENTICATED" | "HTTP_ERROR";
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;
  readonly correlationId: string | undefined;

  constructor(args: {
    message: string;
    code: ApiClientError["code"];
    status: number;
    details?: Record<string, unknown>;
    correlationId?: string;
  }) {
    super(args.message);
    this.code = args.code;
    this.status = args.status;
    this.details = args.details;
    this.correlationId = args.correlationId;
  }
}

export interface ApiFetchOptions {
  readonly method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  readonly query?: Readonly<Record<string, string | number | undefined>>;
  readonly body?: unknown;
  readonly signal?: AbortSignal;
}

const ADMIN_BASE = "/admin";

// Single entry point for every call to the Nest /admin/* API. Attaches the
// admin JWT from the in-memory store, unwraps the { data, meta } envelope on
// success, and maps error responses to ApiClientError.
//
// When running embedded, a 401 triggers exactly one re-mint via App Bridge
// (session tokens are short-lived and expected to rotate). Dev mode just
// bubbles the 401 — the DevAuthGate handles re-sign-in.
export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const envelope = await doFetch<ApiResponse<T>>(path, options, false);
  return envelope.data;
}

export async function apiFetchEnvelope<TEnvelope>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<TEnvelope> {
  return doFetch<TEnvelope>(path, options, false);
}

async function doFetch<T>(
  path: string,
  options: ApiFetchOptions,
  isRetry: boolean,
): Promise<T> {
  const token = getToken();
  if (!token) {
    if (!isRetry && isEmbedded()) {
      await mintSessionAuth();
      return doFetch<T>(path, options, true);
    }
    throw new ApiClientError({
      message: "No admin token — dev auth required.",
      code: "UNAUTHENTICATED",
      status: 401,
    });
  }

  const url = buildUrl(path, options.query);
  const correlationId = crypto.randomUUID();

  const init: RequestInit = {
    method: options.method ?? "GET",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${token}`,
      "x-correlation-id": correlationId,
    },
  };
  if (options.signal) init.signal = options.signal;
  if (options.body !== undefined) init.body = JSON.stringify(options.body);

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    throw new ApiClientError({
      message: err instanceof Error ? err.message : "Network error",
      code: "NETWORK_ERROR",
      status: 0,
      correlationId,
    });
  }

  // Our admin JWT expired mid-session or the backend rejected it. In embedded
  // mode App Bridge can mint a fresh one; retry exactly once to avoid loops.
  if (response.status === 401 && !isRetry && isEmbedded()) {
    clearToken();
    await mintSessionAuth();
    return doFetch<T>(path, options, true);
  }

  if (response.status === 204 || response.status === 205) {
    return undefined as T;
  }

  const text = await response.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new ApiClientError({
        message: `Non-JSON response from ${path} (${response.status})`,
        code: "HTTP_ERROR",
        status: response.status,
        correlationId,
      });
    }
  }

  if (!response.ok || isApiError(parsed)) {
    const err = parsed as ApiError | null;
    const details = err?.error?.details;
    const throwArgs: ConstructorParameters<typeof ApiClientError>[0] = {
      message: err?.error?.message ?? response.statusText ?? "Request failed",
      code: (err?.error?.code ?? "HTTP_ERROR") as ApiClientError["code"],
      status: response.status,
      correlationId: err?.meta?.correlationId ?? correlationId,
    };
    if (details) throwArgs.details = details;
    throw new ApiClientError(throwArgs);
  }

  return parsed as T;
}

export function buildUrl(
  path: string,
  query: ApiFetchOptions["query"],
): string {
  const base = `${ADMIN_BASE}${path.startsWith("/") ? path : `/${path}`}`;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === "") continue;
    params.append(key, String(value));
  }
  const qs = params.toString();
  return qs.length > 0 ? `${base}?${qs}` : base;
}

function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === "object" &&
    value !== null &&
    "error" in value &&
    typeof (value as { error?: unknown }).error === "object"
  );
}
